import { useEffect, useState } from 'react';
import { analytics, AnalyticsIdentityInput } from './analytics';

export function useAnalyticsInitialization(): void {
  useEffect(() => {
    analytics.initialize();
  }, []);
}

export function useAnalyticsIdentity(identity: AnalyticsIdentityInput): void {
  const { userId, accountId } = identity;
  const [consentRevision, setConsentRevision] = useState(0);

  useEffect(() => {
    const sync = () => setConsentRevision(value => value + 1);
    window.addEventListener('assistant-analytics-consent', sync);
    return () => window.removeEventListener('assistant-analytics-consent', sync);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      analytics.clearIdentity();
      return;
    }

    analytics.identify({ userId, accountId }).catch((error) => {
      if (!cancelled) {
        // eslint-disable-next-line no-console
        console.warn('Failed to set analytics identity', error);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [userId, accountId, consentRevision]);
}

export function useAnalyticsPageViews(): void {
  // Route changes are recorded as state rather than tracked inline, so the page
  // view is emitted from an effect — after React has committed the matching
  // identity update — instead of synchronously inside pushState.
  const [routeKey, setRouteKey] = useState<string>(() => window.location.pathname);

  useEffect(() => {
    const sync = () => setRouteKey(window.location.pathname);
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function pushStateWithAnalytics(...args) {
      originalPushState.apply(window.history, args);
      sync();
    };
    window.history.replaceState = function replaceStateWithAnalytics(...args) {
      originalReplaceState.apply(window.history, args);
      sync();
    };
    window.addEventListener('popstate', sync);
    sync();

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', sync);
    };
  }, []);

  useEffect(() => {
    analytics.trackPageView();
  }, [routeKey]);
}
